// client/src/pages/DeleteAccount.jsx
import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { isPlayStoreApp } from "../lib/appMode";

function getStoredToken() {
  try {
    return (
      localStorage.getItem("ga_token") ||
      localStorage.getItem("token") ||
      localStorage.getItem("authToken") ||
      ""
    );
  } catch {
    return "";
  }
}

function getStoredUser() {
  try {
    const raw = localStorage.getItem("user") || localStorage.getItem("ga_user") || "";
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function clearAccountStorage() {
  const keys = [
    "ga_token",
    "token",
    "authToken",
    "jwt",
    "streamsite_token",
    "user",
    "ga_user",
    "email",
    "username",
    "member_email",
    "member_status",
    "member_user",
    "member_username",
  ];

  for (const key of keys) {
    try {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    } catch {}
  }

  window.dispatchEvent(new Event("auth:changed"));
}

export default function DeleteAccount() {
  const navigate = useNavigate();
  const API_BASE = useMemo(() => {
    const raw = (process.env.REACT_APP_API_URL || "").trim();
    const cleaned = raw.replace(/\/$/, "").replace(/\/api$/, "");
    return cleaned || "https://streamsite-v8dd.onrender.com";
  }, []);

  const [token] = useState(() => getStoredToken());
  const [user] = useState(() => getStoredUser());
  const playStoreApp = useMemo(() => isPlayStoreApp(), []);
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [understood, setUnderstood] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [deleted, setDeleted] = useState(false);
  const [deletionId, setDeletionId] = useState("");

  const signedIn = Boolean(token && user);
  const exactConfirmation = confirmation.trim().toUpperCase() === "DELETE";
  const canSubmit = signedIn && password.length > 0 && exactConfirmation && understood && !submitting;

  async function handleDeleteAccount(e) {
    e.preventDefault();
    if (!canSubmit) return;

    const finalOkay = window.confirm(
      "Permanently delete your GAStreaming account? This cannot be undone."
    );
    if (!finalOkay) return;

    setSubmitting(true);
    setError("");

    try {
      const response = await fetch(`${API_BASE}/api/auth/delete-account`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          password,
          confirmation: "DELETE",
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const safePlayMessage =
          playStoreApp && data?.error === "active_wager"
            ? "Resolve your outstanding competition activity on the full GAStreaming website before deleting your account."
            : "";
        throw new Error(safePlayMessage || data?.message || data?.error || "Account deletion failed");
      }

      clearAccountStorage();
      setDeletionId(String(data?.deletionId || ""));
      setDeleted(true);
      setPassword("");
      setConfirmation("");
      setUnderstood(false);
    } catch (err) {
      setError(err?.message || "Account deletion failed");
    } finally {
      setSubmitting(false);
    }
  }

  const card = {
    width: "min(880px, calc(100% - 28px))",
    margin: "0 auto 42px",
    borderRadius: 22,
    padding: "clamp(18px, 4vw, 30px)",
    color: "rgba(255,255,255,0.94)",
    border: "1px solid rgba(255,255,255,0.12)",
    background:
      "radial-gradient(900px 500px at 10% 0%, rgba(255,0,50,0.17), transparent 58%), rgba(8,10,16,0.82)",
    boxShadow: "0 22px 70px rgba(0,0,0,0.58)",
  };

  const inputStyle = {
    width: "100%",
    boxSizing: "border-box",
    borderRadius: 14,
    padding: "13px 14px",
    color: "rgba(255,255,255,0.96)",
    background: "rgba(0,0,0,0.38)",
    border: "1px solid rgba(255,255,255,0.15)",
    outline: "none",
    fontSize: 15,
  };

  const buttonBase = {
    borderRadius: 999,
    padding: "12px 18px",
    border: "1px solid rgba(255,255,255,0.14)",
    fontWeight: 900,
    cursor: "pointer",
  };

  if (deleted) {
    return (
      <main style={card}>
        <div style={{ fontSize: 12, fontWeight: 1000, letterSpacing: 1.4, color: "#ff8d98" }}>
          ACCOUNT DELETED
        </div>
        <h1 style={{ margin: "8px 0 10px", fontSize: "clamp(30px, 6vw, 52px)" }}>
          Your GAStreaming account is gone.
        </h1>
        <p style={{ margin: 0, lineHeight: 1.7, opacity: 0.82 }}>
          Your account and associated personal data were removed. Any completed competition,
          payout, dispute, fraud-prevention, or legally required records that must be retained are
          kept only in a limited or anonymized form.
        </p>
        {deletionId ? (
          <div style={{ marginTop: 14, fontSize: 12, opacity: 0.62 }}>
            Deletion reference: <strong>{deletionId}</strong>
          </div>
        ) : null}
        <button
          type="button"
          style={{ ...buttonBase, marginTop: 20, color: "#fff", background: "linear-gradient(180deg,#d90b19,#86000a)" }}
          onClick={() => navigate("/")}
        >
          Back to GAStreaming
        </button>
      </main>
    );
  }

  return (
    <main style={card}>
      <div style={{ fontSize: 12, fontWeight: 1000, letterSpacing: 1.4, color: "#ff8d98" }}>
        ACCOUNT & DATA
      </div>
      <h1 style={{ margin: "8px 0 10px", fontSize: "clamp(30px, 6vw, 52px)", lineHeight: 1.02 }}>
        Delete your GAStreaming account
      </h1>
      <p style={{ margin: 0, lineHeight: 1.7, opacity: 0.82 }}>
        This permanently removes your GAStreaming login, profile, feed posts, comments,
        notifications, watch-time record, and other account-linked personal data that can be
        safely deleted.
      </p>

      <div
        style={{
          marginTop: 18,
          padding: 16,
          borderRadius: 16,
          border: "1px solid rgba(255,80,80,0.28)",
          background: "rgba(120,0,0,0.20)",
          lineHeight: 1.65,
        }}
      >
        <strong>Before you delete:</strong>
        <div style={{ marginTop: 8, opacity: 0.86 }}>
          Any active GAStreaming Stripe membership is canceled so future recurring charges stop.
          You must first back out of an active tournament team
          {playStoreApp ? "." : " or resolve an active funded wager."} Completed tournament,
          payout, dispute, payment, fraud-prevention, or legally required records may be retained
          in anonymized or limited form where necessary.
        </div>
      </div>

      {!signedIn ? (
        <div style={{ marginTop: 22 }}>
          <div
            style={{
              padding: 16,
              borderRadius: 16,
              border: "1px solid rgba(255,255,255,0.12)",
              background: "rgba(255,255,255,0.045)",
            }}
          >
            <div style={{ fontWeight: 1000, fontSize: 18 }}>Sign in to continue</div>
            <p style={{ margin: "8px 0 0", opacity: 0.8, lineHeight: 1.6 }}>
              For security, account deletion requires a signed-in session and your current password.
            </p>
            <Link
              to="/login?next=/delete-account"
              style={{
                ...buttonBase,
                display: "inline-block",
                marginTop: 14,
                color: "#fff",
                textDecoration: "none",
                background: "linear-gradient(180deg,#d90b19,#86000a)",
              }}
            >
              Sign in to delete account
            </Link>
          </div>
        </div>
      ) : (
        <form onSubmit={handleDeleteAccount} style={{ marginTop: 22, display: "grid", gap: 14 }}>
          <div
            style={{
              padding: 14,
              borderRadius: 14,
              background: "rgba(255,255,255,0.045)",
              border: "1px solid rgba(255,255,255,0.10)",
              fontSize: 14,
            }}
          >
            Signed in as <strong>{user?.email || user?.username || user?.name || "GAStreaming member"}</strong>
          </div>

          {error ? (
            <div
              role="alert"
              style={{
                padding: "12px 14px",
                borderRadius: 14,
                border: "1px solid rgba(255,80,80,0.34)",
                background: "rgba(130,0,0,0.28)",
                fontWeight: 800,
                lineHeight: 1.5,
              }}
            >
              {error}
            </div>
          ) : null}

          <label style={{ display: "grid", gap: 7 }}>
            <span style={{ fontWeight: 900, fontSize: 13 }}>Current password</span>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={inputStyle}
              disabled={submitting}
            />
          </label>

          <label style={{ display: "grid", gap: 7 }}>
            <span style={{ fontWeight: 900, fontSize: 13 }}>
              Type <strong>DELETE</strong> to confirm
            </span>
            <input
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              placeholder="DELETE"
              autoCapitalize="characters"
              style={inputStyle}
              disabled={submitting}
            />
          </label>

          <label
            style={{
              display: "grid",
              gridTemplateColumns: "auto 1fr",
              gap: 10,
              alignItems: "start",
              padding: 14,
              borderRadius: 14,
              background: "rgba(255,255,255,0.035)",
              border: "1px solid rgba(255,255,255,0.09)",
              lineHeight: 1.55,
            }}
          >
            <input
              type="checkbox"
              checked={understood}
              onChange={(e) => setUnderstood(e.target.checked)}
              disabled={submitting}
              style={{ marginTop: 4 }}
            />
            <span>
              I understand this is permanent and I will lose access to this GAStreaming account.
            </span>
          </label>

          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <button
              type="submit"
              disabled={!canSubmit}
              style={{
                ...buttonBase,
                color: "#fff",
                background: "linear-gradient(180deg,#e01424,#7f0008)",
                opacity: canSubmit ? 1 : 0.5,
                cursor: canSubmit ? "pointer" : "not-allowed",
              }}
            >
              {submitting ? "Deleting account..." : "Permanently delete my account"}
            </button>

            <button
              type="button"
              onClick={() => navigate("/me")}
              disabled={submitting}
              style={{ ...buttonBase, color: "#fff", background: "rgba(255,255,255,0.07)" }}
            >
              Keep my account
            </button>
          </div>
        </form>
      )}

      <div style={{ marginTop: 22, paddingTop: 16, borderTop: "1px solid rgba(255,255,255,0.10)", opacity: 0.7, fontSize: 13, lineHeight: 1.6 }}>
        Need help before deleting? Visit the <Link to="/contact" style={{ color: "#ff9aa4" }}>contact page</Link>.
      </div>
    </main>
  );
}
